// Port of ios/Sources/ViewModels/ChannelListDerivation.swift.
//
// Pure, side-effect-free channel-list domain logic per docs/CHANNEL-LIST.md — deliberately
// kept free of Store/NetworkClient/UI so every rule in the spec (DM naming, GM naming,
// unread/mention/muted derivation, category sorting, local filtering) can be unit tested
// directly against plain values, without a database or a running view model.
//
// Usage: `import * as ChannelListDerivation from './channelListDerivation'` reads like Swift.
import type { ChannelMemberRecord, ChannelRecord, PostRecord, UserRecord } from './records';
import type { DisplayChannel } from './channelListDisplayModels';
import * as TeammateNameDisplay from './teammateNameDisplay';
import { EmojiCharacters } from './emojiCharacters';
import { EmojiShortcodes } from './emojiShortcodes';
import { InlineMarkdown } from './inlineMarkdown';
import { currentAlias } from './channelMeta';
import { MessageAttachments } from './messageAttachments';
import { locale, t, tf } from '../i18n/i18n';

// MARK: - Helpers

/**
 * Swift's `String <`: lexicographic over Unicode scalars of the canonically-normalized string.
 * JS `<` compares UTF-16 code units, which orders astral characters differently.
 */
export function swiftStringLess(a: string, b: string): boolean {
  if (a === b) return false;
  const ca = Array.from(a.normalize('NFC'));
  const cb = Array.from(b.normalize('NFC'));
  const n = Math.min(ca.length, cb.length);
  for (let i = 0; i < n; i++) {
    const x = ca[i].codePointAt(0)!;
    const y = cb[i].codePointAt(0)!;
    if (x !== y) return x < y;
  }
  return ca.length < cb.length;
}

function swiftCompare(a: string, b: string): number {
  return swiftStringLess(a, b) ? -1 : swiftStringLess(b, a) ? 1 : 0;
}

const collators = new Map<string, Intl.Collator>();

/** `localizedCaseInsensitiveCompare`: the UI locale's collation, case ignored, accents kept. */
function localizedCaseInsensitiveCompare(a: string, b: string): number {
  const tag = locale();
  let c = collators.get(tag);
  if (!c) {
    c = new Intl.Collator(tag, { sensitivity: 'accent' });
    collators.set(tag, c);
  }
  return c.compare(a, b);
}

// MARK: - Display names

/**
 * The ONE way a person is named (shared/API.md §13.4): the reader's resolved
 * teammate-name-display policy, never a hardcoded order — this deployment parks
 * DEPARTMENTS in `nickname`, and nickname-first titled DMs after departments.
 */
export function userDisplayName(user: UserRecord): string {
  return TeammateNameDisplay.name(user, TeammateNameDisplay.current());
}

/**
 * A DM's `channel.name` is `"<userIdA>__<userIdB>"`. The display name is the OTHER user's
 * name — never the current user's, regardless of which half of the pair they occupy. Returns
 * `unknownUserFallback` if the name doesn't parse or the other user hasn't synced yet, never
 * an empty string (docs/CHANNEL-LIST.md's "the failure mode to avoid").
 */
export function directMessageName(args: {
  channel: Pick<ChannelRecord, 'name'>;
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  unknownUserFallback: string;
}): string {
  const { channel, currentUserId, usersById, unknownUserFallback } = args;
  const parts = channel.name.split('__');
  if (parts.length !== 2) return unknownUserFallback;
  const otherId = parts[0] === currentUserId ? parts[1] : parts[0];
  // A DM whose two halves are the SAME person is Mattermost's personal space — notes to
  // self, links parked for later. A real conversation with a real name, not the
  // "could not resolve anyone" case the fallback is for (Android's rule, verbatim).
  if (otherId === currentUserId) {
    const selfUser = usersById.get(currentUserId);
    if (!selfUser) return unknownUserFallback;
    return tf('channelList.selfDM', userDisplayName(selfUser));
  }
  const user = usersById.get(otherId);
  if (!user) return unknownUserFallback;
  return userDisplayName(user);
}

/**
 * Composes a group-DM name from its OTHER participants (current user excluded), sorted for
 * stable ordering, e.g. "ana, luis, +2". Participants not yet resolved locally are simply
 * skipped (they'll appear once the sync engine resolves them); if none resolve yet, returns
 * `unknownUserFallback`.
 */
export function groupMessageName(args: {
  participantIds: readonly string[];
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  maxNames?: number;
  unknownUserFallback: string;
}): string {
  const { participantIds, currentUserId, usersById, unknownUserFallback } = args;
  const maxNames = args.maxNames ?? 3;
  const names = participantIds
    .filter((id) => id !== currentUserId)
    .map((id) => usersById.get(id))
    .filter((u): u is UserRecord => u !== undefined)
    .map(userDisplayName)
    .sort(swiftCompare);
  if (names.length === 0) return unknownUserFallback;
  if (names.length <= maxNames) return names.join(', ');
  const shown = names.slice(0, maxNames).join(', ');
  return `${shown}, +${names.length - maxNames}`;
}

/**
 * WHO is in a group DM, as faces — the people the row draws as a cluster of overlapping
 * avatars instead of a generic two-silhouette glyph (user, 2026-09-05: "when a chat is of
 * multiple people let's show a group of their profiles, not +something, it's awful").
 *
 * Ordered exactly like `groupMessageName` orders its names — current user dropped, resolved
 * users only, sorted by display name — so the first face belongs to the first name in the
 * title. A cluster whose order disagreed with the title beside it would read as the wrong
 * people entirely.
 *
 * Capped at `max` = THREE (was two until 2026-10-06: the user asked for the third face of a
 * group whose title named three people — a name with no face beside it reads as someone left
 * out). Three is what the 32pt slot holds as a triangle; a fourth face would be ~14pt.
 *
 * The remainder is NOT rendered as "+N" anywhere. The title already lists every name.
 * Mirrors Kotlin's `ChannelListDerivation.groupFaceIds`.
 */
export function groupFaceIds(args: {
  participantIds: readonly string[];
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  max?: number;
}): string[] {
  const { participantIds, currentUserId, usersById } = args;
  const max = args.max ?? 3;
  return participantIds
    .filter((id) => id !== currentUserId)
    .map((id) => usersById.get(id))
    .filter((u): u is UserRecord => u !== undefined)
    .map((u) => ({ id: u.id, name: userDisplayName(u) }))
    .sort((a, b) => swiftCompare(a.name, b.name))
    .slice(0, max)
    .map((u) => u.id);
}

/**
 * Dispatches on channel type per docs/CHANNEL-LIST.md: "O"/"P" render `display_name`
 * directly; "D" and "G" are derived, never the raw (empty/unreliable) `display_name`.
 * A group with a shared alias (shared/API.md §46) is called by it everywhere.
 */
export function displayName(
  channel: Pick<ChannelRecord, 'type' | 'name' | 'displayName'> & { id?: string },
  args: {
    currentUserId: string;
    usersById: ReadonlyMap<string, UserRecord>;
    participantIds: readonly string[];
    unknownUserFallback: string;
  },
): string {
  switch (channel.type) {
    case 'D':
      return directMessageName({
        channel,
        currentUserId: args.currentUserId,
        usersById: args.usersById,
        unknownUserFallback: args.unknownUserFallback,
      });
    case 'G': {
      const alias = channel.id ? currentAlias(channel.id) : null;
      if (alias) return alias;
      return groupMessageName({
        participantIds: args.participantIds,
        currentUserId: args.currentUserId,
        usersById: args.usersById,
        unknownUserFallback: args.unknownUserFallback,
      });
    }
    default:
      return channel.displayName;
  }
}

// MARK: - Unread / mention / muted — three independent states

export interface UnreadState {
  isUnread: boolean;
  mentionCount: number;
  isMuted: boolean;
}

/**
 * Whether a post heads its own conversation rather than replying inside a thread. Stated once
 * because two very different things depend on the same answer: the channel's unread shortcut
 * (`unreadState`) and the channel row's preview glyph.
 *
 * A root post carries an empty/absent `root_id`; the `== id` arm covers a reply reconciled
 * from a pending write, which can briefly carry its own id (the same edge
 * `PostsDAO.latestReplyPerThread` guards against).
 */
export function isRootPost(post: Pick<PostRecord, 'id' | 'rootId'>): boolean {
  const rootId = post.rootId;
  if (rootId == null || rootId === '') return true;
  return rootId === post.id;
}

/**
 * The thread a row's preview belongs to, or null when the row has nothing to disambiguate.
 *
 * Null for a root post (the channel IS where it lives), null when a draft has taken the
 * preview line, and null when nothing is cached. A reply reconciled from a pending send
 * carries its own id as its root, which `isRootPost` already treats as a root — so this
 * never offers to open a "thread" that is really just the message itself.
 */
export function previewThreadRootId(
  latestPost: Pick<PostRecord, 'id' | 'rootId'> | null | undefined,
  hasDraftPreview: boolean,
): string | null {
  if (hasDraftPreview) return null;
  if (!latestPost) return null;
  if (isRootPost(latestPost)) return null;
  if (latestPost.rootId != null && latestPost.rootId !== '') return latestPost.rootId;
  return latestPost.id;
}

/**
 * The counts alone are not trustworthy — `total_msg_count` lives on the channel,
 * `msg_count` on the membership, refreshed on two schedules, and any moment the channel
 * row is newer INVENTS unread out of nothing (Android's reported "17 and 2 over nothing").
 * `last_viewed_at` is the disambiguator, authoritative in a way the counts are not: viewed
 * at-or-after the last post means provably nothing unseen, whatever the counters say — and
 * the same gate clears `mention_count`, the stickiest phantom of all. Below the gate,
 * ROOT counts decide (CollapsedThreads=always_on): a thread reply never bolds the channel.
 */
export function unreadState(
  channel: Pick<ChannelRecord, 'lastPostAt' | 'totalMsgCount'>,
  member: Pick<ChannelMemberRecord, 'markUnread' | 'lastViewedAt' | 'msgCount' | 'mentionCount'> | null | undefined,
  options: {
    /**
     * True when the newest post in this conversation is a ROOT post written by the reader.
     *
     * ROOT is load-bearing, not decoration. Under `CollapsedThreads=always_on` a thread reply
     * does not belong to the channel's unread at all, so "my own newest post" said of a REPLY
     * proves nothing about the root messages above it: answering in one thread would have
     * silently marked a channel full of genuinely unread messages as read. The shortcut is
     * only sound for the newest post the channel's own counters actually count.
     */
    latestPostIsOwnRoot?: boolean;
    /**
     * True when the channel's sidebar CATEGORY is muted — the second, independent mute.
     * Muting a category sets `muted` on the category and touches no member row, so a
     * channel with NO member row at all still counts muted (Android's rule, verbatim).
     */
    categoryMuted?: boolean;
  } = {},
): UnreadState {
  const latestPostIsOwnRoot = options.latestPostIsOwnRoot ?? false;
  const categoryMuted = options.categoryMuted ?? false;
  if (!member) {
    return { isUnread: false, mentionCount: 0, isMuted: categoryMuted };
  }
  const isMuted = member.markUnread === 'mention' || categoryMuted;

  // You cannot have unread messages in a conversation whose newest message is YOURS: you
  // were there, writing it. The server agrees — posting updates the author's own
  // `last_viewed_at` and `msg_count` (verified against the live DB 2026-08-31) — so this
  // states an invariant the counters already imply rather than inventing a new rule. It
  // stands ahead of the counters because a cached member row can lag the truth, and a
  // badge over your own last word is the one unread state that is never right (reported
  // 2026-08-31 with "You:" previews badged 1-5).
  if (latestPostIsOwnRoot) {
    return { isUnread: false, mentionCount: 0, isMuted };
  }

  // Only trust this with both halves: a channel with no posts (lastPostAt == 0) or a
  // membership that never synced its view time tells us nothing, and treating "unknown"
  // as "seen" would hide genuine unread messages.
  const provablySeen =
    channel.lastPostAt > 0 && member.lastViewedAt > 0 && member.lastViewedAt >= channel.lastPostAt;
  if (provablySeen) {
    return { isUnread: false, mentionCount: 0, isMuted };
  }

  // TOTALS, replies included — the user's ruling of 2026-10-05: a message in a thread is a
  // message in the channel, and the channel goes bold. The webapp (CollapsedThreads=
  // always_on) counts roots only; this is the collapsed-threads-OFF rule, on purpose.
  // Opening the channel clears it (`clearUnread` writes both watermarks); the thread stays
  // unread in Hilos. `provablySeen` above keeps an old total diff from inventing unread.
  // Android's `ChannelListDerivation.unreadState` carries the full rationale.
  // A MUTED channel shows no mention badge either — the user's ruling of 2026-10-06 ("if
  // they are muted the notification must not appear, neither the unread status"). Until
  // then a mention escaped the mute the way the webapp's does; muting is now total.
  return {
    isUnread: channel.totalMsgCount - member.msgCount > 0,
    mentionCount: isMuted ? 0 : member.mentionCount,
    isMuted,
  };
}

// MARK: - Previews

/**
 * The row's second line: the last message, flattened to its RENDERED text (a preview
 * showing raw Markdown syntax reads as a bug) and one line. Own messages read "Tú:" even
 * in a DM; channel messages carry the sender; an empty message is an attachment.
 */
export function messagePreview(args: {
  message: string;
  authorName: string | null | undefined;
  isOwnMessage: boolean;
  showsAuthorPrefix: boolean;
  youLabel: string;
  attachmentPlaceholder: string;
}): string {
  const flattened = flattenForOneLine(args.message);
  const body = flattened === '' ? args.attachmentPlaceholder : flattened;

  if (args.isOwnMessage) return args.youLabel + ': ' + body;
  if (args.showsAuthorPrefix && args.authorName) return args.authorName + ': ' + body;
  return body;
}

/**
 * The text a one-line preview is made of: the message, or — when an integration posted an
 * EMPTY message carrying a card — the card's own words (pretext, title, text, first field).
 * Without it every card-only bot post previewed as "Archivo adjunto". Android's
 * `previewSource`.
 */
export function previewSource(message: string, props: string | null | undefined): string {
  if (message.trim() !== '' || !props) return message;
  const card = MessageAttachments.parse(props)[0];
  if (!card) return message;
  const first = card.fields[0];
  const field = first ? first.title + ': ' + first.value : '';
  return [card.pretext, card.title, card.text, field].find((s) => s.trim() !== '') ?? message;
}

/**
 * The row's second line when there is something UNSENT there — a draft OUTRANKS the last
 * message: the message is one tap away either way, the unfinished one is invisible
 * anywhere else. Null for a draft that is empty once flattened: whitespace is not unsent
 * work.
 */
export function draftPreview(draft: string, draftLabel: string): string | null {
  const flattened = flattenedDraft(draft);
  return flattened === null ? null : draftLabel + ': ' + flattened;
}

export function flattenedDraft(draft: string): string | null {
  const flattened = flattenForOneLine(draft);
  return flattened === '' ? null : flattened;
}

/**
 * Raw message Markdown as the plain text a bubble would draw: standard shortcodes become
 * their characters (before the parse, as `MessageBody` does), Markdown syntax goes. For the
 * quoted-message snippets in sheets; rows use `messagePreview`, which also flattens.
 */
export function plainText(markdown: string): string {
  return InlineMarkdown.parse(EmojiShortcodes.substitute(markdown, EmojiCharacters.character)).text;
}

/**
 * Raw message Markdown rendered to the text it shows, squeezed onto one line: standard
 * `:shortcode:` emoji become their characters, whitespace runs collapse to single spaces
 * and inline images disappear entirely.
 *
 * The shortcodes are substituted BEFORE the parse, exactly as `MessageBody` does for the
 * bubble: the wire carries `:moneybag:`, not 💰 (the desktop picker and every bot send
 * names), and a row reading the colons literally while the conversation showed the glyph
 * was the reported bug. A name this client does not know (a custom server emoji) stays as
 * `:name:` — a one-line row draws no images, and the name is the honest fallback.
 *
 * The images have to go rather than degrade to their alt text. A one-line row cannot draw a
 * picture, so the placeholder character would render as the empty ￼ box every font falls back
 * to — and our WhatsApp bridge opens EVERY relayed message with the sender's avatar, so every
 * bridged conversation's preview would have started with one.
 */
function flattenForOneLine(markdown: string): string {
  return plainText(markdown).split(InlineMarkdown.imagePlaceholder).join('').replace(/\s+/g, ' ').trim();
}

// MARK: - Local filter

/**
 * Instant local filter: matches channel display name, DM partner name, or username,
 * case-insensitively. An empty query matches everything.
 */
export function matches(args: {
  query: string;
  displayName: string;
  dmPartnerName: string | null | undefined;
  username: string | null | undefined;
}): boolean {
  const trimmed = args.query.trim();
  if (trimmed === '') return true;
  const needle = trimmed.toLowerCase();
  if (args.displayName.toLowerCase().includes(needle)) return true;
  if (args.dmPartnerName != null && args.dmPartnerName.toLowerCase().includes(needle)) return true;
  if (args.username != null && args.username.toLowerCase().includes(needle)) return true;
  return false;
}

// MARK: - Category channel ordering

/**
 * Orders a category's member channel ids: NEWEST ACTIVITY FIRST, whatever the category's server
 * `sorting` says (`manual` / `alpha` / `recent` alike — user ruling 2026-10-17: "The channels must
 * always be listed by date from the newest to the oldest activity"; a «Channels» category of WA
 * channels in manual order showed «mié» rows above «jue»). `lastPostAtFor` must be the SAME value
 * the row stamps (DisplayChannel.lastPostAt). Ties: display name (case-insensitive), then id, so the
 * order is stable. Unread does not move a row.
 */
export function sortedChannelIds(
  channelIds: readonly string[],
  args: {
    displayNameFor: (id: string) => string;
    lastPostAtFor: (id: string) => number;
  },
): string[] {
  const { displayNameFor, lastPostAtFor } = args;
  return [...channelIds].sort(
    (a, b) => lastPostAtFor(b) - lastPostAtFor(a) || localizedCaseInsensitiveCompare(displayNameFor(a), displayNameFor(b)) || (a < b ? -1 : a > b ? 1 : 0),
  );
}

// MARK: - Collapsed categories

/**
 * FINAL ruling 2026-08-25: a collapsed category still surfaces `(unread OR mentioned) AND
 * NOT muted` rows, like the webapp — muted never escapes (the screenshotted bug). Search
 * overrides everything: a match the user cannot see is a search that appears broken.
 *
 * NOTE: docs/CHANNEL-LIST.md records a 2026-08-24 order that a collapsed category hides ALL its
 * rows; the Swift code (the web's reference) and its tests implement the 2026-08-25 ruling
 * above, so this port does too.
 */
export function visibleRows(
  rows: readonly DisplayChannel[],
  args: { collapsed: boolean; isSearching: boolean; selectedId?: string | null },
): DisplayChannel[] {
  if (args.isSearching || !args.collapsed) return [...rows];
  const selectedId = args.selectedId ?? null;
  // The OPEN conversation outranks the collapse (and even its own mute): collapsing the
  // category the reader is standing in must not vanish the chat from under them — it slips
  // away only once they move somewhere else (user report 2026-09-05; the webapp's rule).
  return rows.filter((r) => r.id === selectedId || ((r.isUnread || r.mentionCount > 0) && !r.isMuted));
}

/**
 * The Android rule, verbatim (its `ChannelListDerivation.categoryDisplayName`): dispatch on
 * the category `type` — a stable server enum — NOT on the English string. Matching
 * "Favorites" by name would silently re-translate a user-created category that happens to be
 * called that, and would break the moment the server's own copy changes. Anything not a
 * known default is the user's own name and is shown exactly as they typed it.
 */
export function categoryDisplayName(type: string, serverDisplayName: string): string {
  switch (type) {
    case 'favorites':
      return t('channelList.category.favorites');
    case 'channels':
      return t('channelList.category.channels');
    case 'direct_messages':
      return t('channelList.category.directMessages');
    default:
      return serverDisplayName;
  }
}

/** Namespace form, for `ChannelListDerivation.unreadState(...)` call sites (the sibling modules' style). */
export const ChannelListDerivation = {
  userDisplayName,
  directMessageName,
  groupMessageName,
  groupFaceIds,
  displayName,
  isRootPost,
  previewThreadRootId,
  unreadState,
  messagePreview,
  previewSource,
  draftPreview,
  flattenedDraft,
  plainText,
  matches,
  sortedChannelIds,
  visibleRows,
  categoryDisplayName,
} as const;
